import { TransferableAddress, TransferableEmail, TransferableHealthIdentifier, TransferableLookup, TransferablePatientInfoResponse, TransferablePhone } from "@/Common/Api/ExternalCommunications/Dto/TransferablePatientInfoResponse";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { AppointmentContactChannel } from "@/Common/Models/Appointments/AppointmentContactChannel";
import { AppointmentSnapshotContact } from "@/Common/Models/Appointments/AppointmentPatientSnapshot";
import { IImportablePatient } from "@/Common/Models/Patient/IPatient";
import { IPatientCreationModel } from "@/Common/Models/Patient/UI/IPatientCreationModel";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { EnumGender } from "./Enum/EnumGender";

class ThirdPartyPatient {
    public Id: string;
    public PatientContactFacetId: string;
    public ExternalPatientId: string;
    public ExternalSource: number;
    public FirstName: string;
    public PreferredName: string;
    public LastName: string;
    public Gender: EnumGender;
    public Email: string;
    public DateOfBirth: Date;
    public CreatedBy: string;
    public StreetAddress: string;
    public City: string;
    public PostalCode: string;
    public IsoProvinceCode: string;
    public HealthCardNumber: string;
    public HealthCardExpiry: string;
    public LastModified: Date;
}

class ImportableThirdPartyPatient extends ThirdPartyPatient implements IImportablePatient {
    private readonly PersonalContactTypeLookup = cacheStore.FindLookupByLookupCode("PERSONAL", "CONTACTPOINTTYPE");

    private readonly MobilePhoneTypeLookup = cacheStore.FindLookupByLookupCode("MOBILE", "PHONETYPE");
    private readonly PhonePhoneTypeLookup = cacheStore.FindLookupByLookupCode("PHONE", "PHONETYPE");

    private readonly MaleGenderLookup = cacheStore.FindLookupByLookupCode("MALE", "Gender");
    private readonly FemaleGenderLookup = cacheStore.FindLookupByLookupCode("FEMALE", "Gender");
    private readonly OtherGenderLookup = cacheStore.FindLookupByLookupCode("OTHERGENDER", "Gender");

    public ClientTag: string;
    public AppointmentContactData: AppointmentSnapshotContact[];

    public get PatientId(): string {
        return this.Id;
    }

    public get NAM(): string {
        return this.HealthCardNumber;
    }

    public isInternal(): boolean {
        return false;
    }

    public toPatientCreationModel(): IPatientCreationModel {
        const transferable = this.toTransferable();

        const patientCreationModel = PatientHelper.getDefaultPatientCreationModel();

        patientCreationModel.FirstName = transferable.FirstName;
        patientCreationModel.PreferredName = transferable.PreferredName;
        patientCreationModel.LastName = transferable.LastName;
        patientCreationModel.GenderLookupId = transferable.Gender?.Id;
        patientCreationModel.DOB = transferable.DateOfBirth;

        const email = transferable.Emails[0];
        if (email) {
            patientCreationModel.EmailEdit = email.Address;
            patientCreationModel.EmailNotificationsEnabled = true;
            patientCreationModel.EmailTypeByLookup = email.ContactType?.Id;
        }

        const phone1 = transferable.Phones[0];
        const phone2 = transferable.Phones[1];

        if (phone1) {
            patientCreationModel.Phone1Edit = phone1.PhoneNumber;
            patientCreationModel.Phone1Extension = phone1.Extension;
            patientCreationModel.Phone1ContactTypeLookupId = phone1.ContactType?.Id;
            patientCreationModel.Phone1TypeLookupId = phone1.Type?.Id;
            patientCreationModel.Phone1NotificationsEnabled = phone1.Type?.Code === this.MobilePhoneTypeLookup.LookupCode;
        }

        if (phone2) {
            patientCreationModel.Phone2Edit = phone2.PhoneNumber;
            patientCreationModel.Phone2Extension = phone2.Extension;
            patientCreationModel.Phone2ContactTypeLookupId = phone2.ContactType?.Id;
            patientCreationModel.Phone2TypeLookupId = phone2.Type?.Id;
            patientCreationModel.Phone2NotificationsEnabled = phone2.Type?.Code === this.MobilePhoneTypeLookup.LookupCode;
        }

        const address = transferable.Addresses[0];
        if (address) {
            patientCreationModel.CivicAddressEdit = address.StreetAddress;
            patientCreationModel.CityEdit = address.City;
            patientCreationModel.PostalCodeEdit = address.PostalCode;
            patientCreationModel.SelectedRegionLookupId = address.Region?.Id;
            patientCreationModel.SelectedCountryLookupId = address.Country?.Id;
        }

        const healthCard = transferable.HealthIdentifiers[0];
        if (healthCard) {
            patientCreationModel.NAMEdit = healthCard.Value;
            patientCreationModel.NAMExpiryEdit = healthCard.ExpiryDate;
            patientCreationModel.selectedHealthCardRegionLookupId = healthCard.Province?.Id;
        }

        return Immutable.fromJS(patientCreationModel);
    }

    public toTransferable(): TransferablePatientInfoResponse {
        let phones: TransferablePhone[] = [];
        let emails: TransferableEmail[] = [];

        if (this.AppointmentContactData) {
            phones = this.getAppointmentContactPhones(this.AppointmentContactData);
            emails = this.getAppointmentContactEmail(this.AppointmentContactData);
        }
        else if (this.Email) {
            emails = this.mapToTransferableEmails();
        }

        return {
            FirstName: this.FirstName,
            PreferredName: this.PreferredName,
            LastName: this.LastName,
            DateOfBirth: this.DateOfBirth,
            ContactFacetId: null,
            ContactId: null,
            MotherFirstName: null,
            MotherLastName: null,
            Phones: phones,
            Emails: emails,
            Prefix: null,
            PreferredSpokenLanguage: null,
            Gender: this.mapGenderToTransferable(),
            Addresses: this.mapToTransferableAddresses(),
            HealthIdentifiers: this.mapToTransferableHealthIdentifiers()
        };
    }

    /**
     * Get the transferrable phone numbers for the given appointment contact data.
     * @param contactData The appointment contact data
     */
    private getAppointmentContactPhones(contactData: AppointmentSnapshotContact[]): TransferablePhone[] {
        const regionAndCountry = this.mapToTransferableRegionAndCountry();

        return contactData
            .filter(p => p.Action === AppointmentContactChannel.Phone || p.Action === AppointmentContactChannel.Sms)
            .map(p => ({
                PhoneNumber: p.Value,
                IsPreferred: p.IsPreferred,
                ContactType: TransferableLookup.fromLookup(this.PersonalContactTypeLookup),
                Type: TransferableLookup.fromLookup(
                    p.Action === AppointmentContactChannel.Sms
                        ? this.MobilePhoneTypeLookup
                        : this.PhonePhoneTypeLookup
                ),
                Country: regionAndCountry?.country,
                Extension: null
            }));
    }

    /**
     * Get the transferrable emails for the given appointment contact data.
     * @param contactData The appointment contact data
     */
    private getAppointmentContactEmail(contactData: AppointmentSnapshotContact[]): TransferableEmail[] | null {
        return contactData
            .filter(p => p.Action === AppointmentContactChannel.Email)
            .map(email => ({
                Address: email.Value,
                IsOkToContact: false,
                ContactType: TransferableLookup.fromLookup(this.PersonalContactTypeLookup)
            }));
    }

    /**
     * Map the current transferrable patient to a transferrable lookup for the gender.
     */
    private mapGenderToTransferable(): TransferableLookup {
        switch (EnumHelper.getValue(EnumGender, this.Gender)) {
            case EnumGender.Male:
                return TransferableLookup.fromLookup(this.MaleGenderLookup);

            case EnumGender.Female:
                return TransferableLookup.fromLookup(this.FemaleGenderLookup);

            case EnumGender.Other:
                return TransferableLookup.fromLookup(this.OtherGenderLookup);

            default:
                return null;
        }
    }

    /**
     * Map the current transferrable patient to the region and country
     */
    private mapToTransferableRegionAndCountry(): { region: TransferableLookup, country: TransferableLookup } | null {
        const state = cacheStore.GetStateMedfarDictionariesBySubdivisionCode(this.IsoProvinceCode);

        if (!state)
            return null;

        const countryLookupCode = cacheStore.GetCountryLookupById_medfarDictionaries(state.CountryId);

        const regionLookup = cacheStore.FindLookupByLookupCode(state.Myle_LookupCode, state.Myle_LookupTypeCode);
        const countryLookup = cacheStore.FindLookupByLookupCode(countryLookupCode);

        return {
            region: TransferableLookup.fromLookup(regionLookup),
            country: TransferableLookup.fromLookup(countryLookup)
        };
    }

    /**
     * Map the current transferrable patient to an array of transferrable addresses.
     */
    private mapToTransferableAddresses(): TransferableAddress[] {
        const regionAndCountry = this.mapToTransferableRegionAndCountry();

        if (!regionAndCountry)
            return [];

        return [{
            StreetAddress: this.StreetAddress,
            City: this.City,
            PostalCode: this.PostalCode,
            Region: regionAndCountry.region,
            Country: regionAndCountry.country,
            IsPreferred: false
        }];
    }

    /**
     * Map the current transferrable patient to an array of transferrable emails.
     */
    private mapToTransferableEmails(): TransferableEmail[] {
        if (!this.Email)
            return [];

        return [{
            Address: this.Email,
            IsOkToContact: false,
            ContactType: TransferableLookup.fromLookup(this.PersonalContactTypeLookup)
        }];
    }

    /**
     * Map the current transferrable patient to an array of transferrable health identifiers.
     */
    private mapToTransferableHealthIdentifiers(): TransferableHealthIdentifier[] {
        const regionAndCountry = this.mapToTransferableRegionAndCountry();

        if (!regionAndCountry)
            return [];

        const expiryDate = ImportableThirdPartyPatient.parseExpiryDate(this.HealthCardExpiry);
        return [{
            Province: regionAndCountry.region,
            ExpiryDate: expiryDate,
            Value: this.HealthCardNumber
        }];
    }

    private static parseExpiryDate(healthCardExpiry: string): Date | null {
        const parsedDate = LuxonDateTimeParser.parse(healthCardExpiry);

        return parsedDate?.isValid
            ? new Date(parsedDate.year, parsedDate.month + 1, 0)
            : null;
    }
}

export { ImportableThirdPartyPatient, ThirdPartyPatient };
